import { Divider } from "@heroui/react";
import { motion, useReducedMotion } from "framer-motion";
import { type LucideIcon, X } from "lucide-react";
import { type JSX } from "react";

import { Button } from "./core/Button";

export const SIDEBAR_SPRING = { type: "spring", stiffness: 50 } as const;

/**
 * Information sidebar
 *
 * TODO: add customization for this post-event (#46)
 *
 * @returns
 */
export const SidebarBase = ({
  children,
  shown,
  icon: Icon,
  setSidebarShown,
  title,
  description,
  side,
  width,
}: {
  children: string | JSX.Element | JSX.Element[];
  icon: LucideIcon;
  shown: boolean;
  setSidebarShown: (value: boolean) => void;
  title: string;
  description?: string;
  side: "Left" | "Right";
  width?: string;
}) => {
  const reduceMotion = useReducedMotion() ?? false;

  return (
    <div>
      <motion.div
        style={{ width }}
        className={`min-w-[20rem] max-w-[75vw] md:max-w-[30vw] bg-white dark:bg-black flex flex-col justify-between fixed ${side === "Left" ? "left-0" : "right-0"}  h-full shadow-xl overflow-y-auto z-50 top-0`}
        initial={{ x: side === "Left" ? "-150%" : "150%" }}
        animate={{
          x: shown
            ? side === "Left"
              ? "-50%"
              : "50%"
            : side === "Left"
              ? "-150%"
              : "150%",
        }}
        transition={
          reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 50 }
        }
      />
      <motion.div
        style={{ width }}
        aria-hidden={!shown}
        data-sidebar-side={side}
        className={`canvas-sidebar canvas-sidebar--${side.toLowerCase()} min-w-[20rem] max-w-[75vw] md:max-w-[30vw] bg-white dark:bg-black text-black dark:text-white flex flex-col fixed ${side === "Left" ? "left-0" : "right-0"} h-full shadow-xl overflow-y-auto z-50 top-0`}
        initial={{ x: side === "Left" ? "-100%" : "100%" }}
        animate={{ x: shown ? 0 : side === "Left" ? "-100%" : "100%" }}
        transition={
          reduceMotion ? { duration: 0 } : SIDEBAR_SPRING
        }
      >
        <div className="sticky top-0 z-20 bg-white dark:bg-black">
          <header className="flex p-4 justify-between items-center">
            <div>
              <div className="flex items-center gap-2">
                <Icon aria-hidden="true" size={22} />
                <div>
                  <h1 className="text-xl">{title}</h1>
                  {description && (
                    <p className="text-xs text-default-600">{description}</p>
                  )}
                </div>
              </div>
            </div>

            <Button
              size="sm"
              isIconOnly
              onPress={() => {
                setSidebarShown(false);
              }}
              className="ml-4"
            >
              <X aria-hidden="true" size={18} />
            </Button>
          </header>
          <Divider />
        </div>
        {children}
      </motion.div>
    </div>
  );
};
